import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { FaSearch } from 'react-icons/fa';
import Table from '../../components/Table';
import Input from '../../components/Input';
import Checkbox from '../../components/Checkbox';
import Button from '../../components/Button';
import Pagination from '../../components/Pagination';
import ItemImage from '../../components/ItemImage';
import ErrorMessages from '../../components/ErrorMessages.js';
import Paths from '../../constants/paths';
import translations from '../../translations/de';
import requsetErrorMessage from '../../helpers/requsetErrorMessage';
import API from '../../api/teams';

// Players of all teams, to find one who changed clubs and move him instead of
// creating him a second time.
const PlayersSearch = ({ store }) => {
    const history = useHistory();
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    // off by default, a deleted player is one tick away when needed
    const [showDeleted, setShowDeleted] = useState(false);
    const [players, setPlayers] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError('');
        API.searchPlayers({
            params: {
                search: search || undefined,
                with_deleted: showDeleted || undefined,
                page,
                limit: 20,
            },
        })
            .then((res) => {
                if (cancelled) return;
                setPlayers(res.data.items);
                setTotalPages(res.data.total_pages_count);
            })
            .catch((err) => {
                if (!cancelled)
                    setError(requsetErrorMessage(err && err.message ? err.message : err));
            })
            .finally(() => !cancelled && setIsLoading(false));
        return () => {
            cancelled = true;
        };
    }, [search, page, showDeleted]);

    const onSearchChange = useMemo(
        () =>
            debounce((e) => {
                setPage(1);
                setSearch(e.target.value.trim());
            }, 500),
        [],
    );

    const openPlayer = (player) => {
        store.selectTeam(undefined);
        store.selectPlayer(undefined);
        history.push(
            player.team_id
                ? `/teams/${player.team_id}/players/${player.id}`
                : `${Paths.OUT_OF_LEAGUE_PLAYERS}/${player.id}`,
        );
    };

    return (
        <>
            <div style={{ marginLeft: '2.5%' }}>
                <ErrorMessages messages={error} />
            </div>
            <div className="tab-controls">
                <Input
                    placeholder={translations.search}
                    onChange={onSearchChange}
                    label={`${translations.first_name} / ${translations.last_name} / ${translations.team}`}
                />
                <Checkbox
                    // level with the search field: under it the Input keeps
                    // 13px for an error line and 10px of margin, and the 21px
                    // box is centred on the 35px field
                    outerStyle={{ alignSelf: 'flex-end', marginBottom: '30px', marginLeft: '20px' }}
                    text={translations.show_deleted}
                    checked={showDeleted}
                    onChange={(e) => {
                        setPage(1);
                        setShowDeleted(e.target.checked);
                    }}
                />
            </div>
            <Table
                isLoading={isLoading}
                tableName={translations.players}
                tablePagination={
                    totalPages > 1 && (
                        <Pagination
                            totalPages={totalPages}
                            selectedPage={page}
                            onSelectedPageChange={setPage}
                        />
                    )
                }
                columns={[
                    {
                        Header: 'ID',
                        accessor: 'id',
                        Cell: ({ value }) => <div style={{ whiteSpace: 'nowrap' }}>{value}</div>,
                    },
                    {
                        Header: translations.first_name,
                        accessor: 'first_name',
                        Cell: ({ value }) => <div>{value}</div>,
                    },
                    {
                        Header: translations.last_name,
                        accessor: 'last_name',
                        Cell: ({ value, row }) => (
                            <div>
                                {value}
                                {row.original.deleted_at ? ` (${translations.deleted})` : ''}
                            </div>
                        ),
                    },
                    {
                        Header: translations.number,
                        accessor: 'number',
                        Cell: ({ value }) => <div>{value}</div>,
                    },
                    {
                        Header: translations.position,
                        accessor: 'position',
                        Cell: ({ value }) => <div>{value}</div>,
                    },
                    {
                        Header: translations.logo,
                        accessor: 'logo_url',
                        Cell: ({ value }) =>
                            value ? (
                                <div>
                                    <ItemImage imgSrc={value} alt="team" listView={true} />
                                </div>
                            ) : null,
                    },
                    {
                        Header: translations.team,
                        accessor: 'team_title',
                        Cell: ({ value }) => <div>{value || translations.no_league}</div>,
                    },
                    {
                        Header: '',
                        accessor: 'action',
                        Cell: ({ row }) => (
                            <div>
                                <Button
                                    onClick={() => openPlayer(row.original)}
                                    title={translations.show}
                                >
                                    <FaSearch />
                                </Button>
                            </div>
                        ),
                    },
                ]}
                data={players.map((player) => ({
                    id: player.id,
                    first_name: player.first_name,
                    last_name: player.last_name,
                    number: player.number,
                    position: player.position,
                    logo_url: player.team?.logo_url,
                    team_title: player.team?.title,
                    team_id: player.team_id,
                    deleted_at: player.deleted_at,
                }))}
            />
        </>
    );
};

export default PlayersSearch;
